import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, Trash2, Edit2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function Categories() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [editing, setEditing] = useState(null);

  async function load() { const { data } = await api.get("/categories"); setItems(data); }
  useEffect(() => { load(); }, []);

  async function add() {
    if (!name.trim()) return;
    try { await api.post("/categories", { name, description: desc }); setName(""); setDesc(""); toast.success("Category created"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }
  async function save() {
    try { await api.put(`/categories/${editing.id}`, { name: editing.name, description: editing.description || "" }); setEditing(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }
  async function del(id) {
    if (!window.confirm("Delete this category?")) return;
    try { await api.delete(`/categories/${id}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }

  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="categories-page">
      <header className="mb-8">
        <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-2">/ TAXONOMY · COLLECTIONS</div>
        <h1 className="section-title text-5xl lg:text-6xl">Categories.</h1>
      </header>

      <div className="brut-card p-6 mb-8 max-w-2xl">
        <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-3">Add new category</div>
        <div className="flex flex-col md:flex-row gap-3">
          <input className="brut-input md:flex-1" placeholder="e.g. F1 Tees" value={name} onChange={(e) => setName(e.target.value)} data-testid="new-category-name" />
          <input className="brut-input md:flex-1" placeholder="Short description" value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="new-category-desc" />
          <button onClick={add} className="brut-btn px-4 py-2 inline-flex items-center gap-2" data-testid="add-category-btn"><Plus size={16} /> Add</button>
        </div>
      </div>

      <div className="brut-card overflow-hidden max-w-3xl">
        <table className="brut-table">
          <thead><tr><th>Name</th><th>Description</th><th className="w-32">Actions</th></tr></thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id}>
                {editing?.id === c.id ? (
                  <>
                    <td><input className="brut-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></td>
                    <td><input className="brut-input" value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></td>
                    <td className="flex gap-1">
                      <button onClick={save} className="p-2 bg-[#00C853] border-2 border-black"><Check size={14} /></button>
                      <button onClick={() => setEditing(null)} className="p-2 border-2 border-black"><X size={14} /></button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="font-display font-bold">{c.name}</td>
                    <td className="text-sm">{c.description}</td>
                    <td className="flex gap-1">
                      <button onClick={() => setEditing(c)} className="p-2 border-2 border-black hover:bg-zinc-100" data-testid={`edit-cat-${c.id}`}><Edit2 size={14} /></button>
                      {user?.role === "admin" && <button onClick={() => del(c.id)} className="p-2 border-2 border-black hover:bg-[#FF3333] hover:text-white" data-testid={`delete-cat-${c.id}`}><Trash2 size={14} /></button>}
                    </td>
                  </>
                )}
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={3} className="text-center py-8 text-zinc-500 font-mono-ds text-xs uppercase">No categories yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
